"use client";
/**
 * SANATAN DECODE — v11 "PAPER". 21s @ 30fps, 1280x720.
 *
 * Same audio, same word map, same beat windows and same motion laws as v10.
 * What changes is the MATERIAL, taken from the Vox-collage reference: every cutout and every
 * headline is now physically PASTED — a hand-torn paper card with a white core edge, a cast
 * shadow and (sparingly) a strip of tape — instead of floating alpha PNGs over a photograph.
 *
 * Why this is not "more shapes" (LAW 8): nothing new is introduced into the frame. The same
 * five cutouts and the same words are simply given a surface to sit on. That surface is also
 * the strongest available answer to LAW 5 (background must never merge with foreground) —
 * a torn opaque card separates by MATERIAL, where v10 separated by a cream text-halo.
 *
 * Retained verbatim from v10: on twos, swing physics, object permanence + graphic match,
 * one accent per beat, anchor-first, the asymmetric panorama walk.
 */
import React from "react";
import {
  AbsoluteFill, Audio, Img, staticFile,
  useCurrentFrame, useVideoConfig, interpolate, spring, Easing, random,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: deva } = loadDeva("normal", { weights: ["400", "600", "700", "800", "900"], subsets: ["devanagari", "latin"] });
const { fontFamily: inter } = loadInter("normal", { weights: ["500", "700", "800"], subsets: ["latin"] });

const INK = "#22304F";
const GOLD = "#BE9433";
const MARIGOLD = "#D98324";
const KUMKUM = "#C0392B";
const MUTED_C = "#6A6250";
const PAPER = "#FBF3E0";          // the card stock
const PAPER_EDGE = "#FFFDF6";     // the white torn core, offset behind the card

const FPS = 30;
const f = (s: number) => s * FPS;
const useStep = () => Math.floor(useCurrentFrame() / 2) * 2;

const W: Record<number, [number, number, string]> = {
  0: [0.0, 0.15, "आप"], 1: [0.21, 0.35, "जिस"], 2: [0.41, 0.71, "गोत्र"], 3: [0.74, 0.82, "का"],
  4: [0.87, 1.06, "नाम"], 5: [1.13, 1.36, "लेते"], 6: [1.42, 1.64, "हैं"],
  8: [2.03, 2.16, "वो"], 9: [2.26, 2.47, "आपका"], 10: [2.58, 3.01, "सरनेम"], 11: [3.05, 3.24, "नहीं"],
  12: [3.32, 3.6, "है।"],
  15: [4.35, 4.7, "ऋषि"],
  26: [8.2, 8.42, "एक"], 27: [8.45, 8.52, "ही"], 28: [8.63, 9.17, "रक्त-रेखा"],
  34: [11.61, 12.03, "शास्त्र"],
  38: [13.41, 14.03, "सगोत्र"], 39: [14.41, 14.76, "विवाह"], 40: [14.86, 15.27, "नहीं।"],
  45: [17.43, 17.86, "विज्ञान"],
};

const useKeys = (keys: [number, number][]) => {
  const frame = useStep();
  return interpolate(frame, keys.map((k) => f(k[0])), keys.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });
};

const useSpringAt = (at: number, damping = 9, stiffness = 110, mass = 1.1) => {
  const frame = useStep();
  const { fps } = useVideoConfig();
  return spring({ frame: frame - f(at), fps, config: { damping, stiffness, mass } });
};

/* ══ THE TORN EDGE ═════════════════════════════════════════════
   A deterministic jagged polygon walked clockwise around the card. `amp` is the tear depth
   as a percentage of the card, so small cards tear finely and big panels tear coarsely.
   Seeded off the card's name → identical every frame, so the paper never boils.            */
const tornClip = (seed: string, amp = 1.5, n = 14) => {
  const p: string[] = [];
  const j = (t: string, i: number) => random(`${seed}${t}${i}`) * amp;
  for (let i = 0; i <= n; i++) p.push(`${((i / n) * 100).toFixed(1)}% ${j("T", i).toFixed(2)}%`);
  for (let i = 1; i <= n; i++) p.push(`${(100 - j("R", i)).toFixed(2)}% ${((i / n) * 100).toFixed(1)}%`);
  for (let i = 1; i <= n; i++) p.push(`${(100 - (i / n) * 100).toFixed(1)}% ${(100 - j("B", i)).toFixed(2)}%`);
  for (let i = 1; i < n; i++) p.push(`${j("L", i).toFixed(2)}% ${(100 - (i / n) * 100).toFixed(1)}%`);
  return `polygon(${p.join(",")})`;
};

const CARD_SHADOW = "drop-shadow(0 18px 20px rgba(72,46,16,0.34)) drop-shadow(0 2px 2px rgba(72,46,16,0.30))";

/** A hand-torn paper card. The white core sits 3px behind and 0.7° off, which is what makes
    the eye read "torn" rather than "clipped". */
const Card: React.FC<{
  seed: string; w?: number | string; h?: number | string; bg?: string; amp?: number;
  tilt?: number; pad?: string; style?: React.CSSProperties; children?: React.ReactNode;
}> = ({ seed, w, h, bg = PAPER, amp = 1.5, tilt = 0, pad = "0px", style, children }) => (
  <div style={{ position: "relative", width: w, height: h, transform: `rotate(${tilt}deg)`, ...style }}>
    <div style={{
      position: "absolute", inset: -4, background: PAPER_EDGE,
      clipPath: tornClip(seed + "core", amp * 1.25),
      transform: "rotate(0.7deg)", filter: CARD_SHADOW,
    }} />
    <div style={{
      position: "relative", width: "100%", height: "100%", background: bg,
      clipPath: tornClip(seed, amp), padding: pad, boxSizing: "border-box",
    }}>{children}</div>
  </div>
);

/** Tape. Two per composition, no more — it is punctuation, not decoration. */
const Tape: React.FC<{ x: number; y: number; w?: number; rot?: number; op?: number }> =
  ({ x, y, w = 92, rot = -24, op = 0.5 }) => (
    <div style={{
      position: "absolute", left: x, top: y, width: w, height: 27,
      background: "linear-gradient(180deg, rgba(238,225,196,0.92) 0%, rgba(225,210,176,0.80) 100%)",
      opacity: op, transform: `rotate(${rot}deg)`,
      clipPath: "polygon(3% 0%, 97% 4%, 100% 96%, 1% 100%)",
      boxShadow: "0 2px 4px rgba(72,46,16,0.25)",
    }} />
  );

/* ══ THE GROUND — one panorama, walked (unchanged from v10) ════ */
const PANO_W = 3840, PANO_H = 1280;
const DISPLAY_H = 1240;
const DISPLAY_W = (PANO_W / PANO_H) * DISPLAY_H;
const TRAVEL = DISPLAY_W - 1280;

const WALK: [number, number][] = [
  [0.00, 0.0000], [3.60, 0.0492], [4.15, 0.4180], [5.45, 0.3443], [6.05, 0.8156],
  [10.25, 0.4795], [10.85, 0.8893], [15.45, 0.5205], [16.05, 1.0000], [21.20, 0.8770],
];
const walkAt = (fr: number) =>
  interpolate(fr, WALK.map((k) => f(k[0])), WALK.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });

const Pano: React.FC = () => {
  const frame = useStep();
  const x = walkAt(frame);
  const vx = (Math.abs(x - walkAt(Math.max(0, frame - 2))) * TRAVEL) / 2;
  const whip = Math.min(20, vx * 0.16);
  const y = Math.sin(frame / 78) * 7;
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      <Img
        src={staticFile("sanatan/pano/gurukul_pano.png")}
        style={{
          position: "absolute", height: DISPLAY_H, width: DISPLAY_W,
          left: -x * TRAVEL, top: -300 + y, maxWidth: "none",
          // pushed further back than v10 — the cards now carry the foreground, so the ground
          // can afford to be softer without the frame going empty.
          filter: `blur(${(4.6 + whip).toFixed(2)}px) saturate(0.60) brightness(1.13) contrast(0.82)`,
        }}
      />
      <AbsoluteFill style={{ backgroundColor: "rgba(249,240,222,0.34)" }} />
      <AbsoluteFill style={{ background: "radial-gradient(ellipse 84% 78% at 50% 46%, rgba(255,252,242,0.30) 0%, rgba(198,158,100,0.32) 100%)" }} />
    </AbsoluteFill>
  );
};

const Grain: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity: 0.14, mixBlendMode: "multiply" }}>
      <svg width="100%" height="100%">
        <filter id="gr11"><feTurbulence type="fractalNoise" baseFrequency="0.92" numOctaves="3" seed={frame % 5} /></filter>
        <rect width="100%" height="100%" filter="url(#gr11)" />
      </svg>
    </AbsoluteFill>
  );
};

const Petals: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 14 }, (_, i) => {
        const seed = `v11${i}`;
        const x = random(seed) * 1280;
        const sp = 0.4 + random(seed + "s") * 0.7;
        const y = ((random(seed + "y") * 800 + frame * sp) % 830) - 60;
        const sway = Math.sin((frame + i * 40) / 34) * 30;
        const r = 3 + random(seed + "r") * 4.5;
        return <div key={i} style={{
          position: "absolute", left: x + sway, top: y, width: r * 2, height: r * 1.25,
          borderRadius: "50%", background: MARIGOLD, opacity: 0.22,
          transform: `rotate(${frame * 1.4 + i * 30}deg)`,
        }} />;
      })}
    </AbsoluteFill>
  );
};

/* ══ THE BEAT PANEL — the repo's "bold flat colour per beat" ═══
   One large torn field per beat, swinging in from an off-frame pivot exactly like a prop.
   It is the type's ground. It is NEVER a full-frame wash — the walked panorama must stay
   visible down at least one edge, or we are back to a flat vector film.                    */
const Panel: React.FC<{
  seed: string; at: number; out: number; x: number; y: number; w: number; h: number;
  bg?: string; from?: "left" | "right" | "top" | "bottom"; tilt?: number; amp?: number;
  children?: React.ReactNode;
}> = ({ seed, at, out, x, y, w, h, bg = PAPER, from = "left", tilt = 0, amp = 1.4, children }) => {
  const frame = useStep();
  const sIn = useSpringAt(at, 9, 88, 1.2);
  const sOut = useSpringAt(out, 200, 72, 0.55);
  if (frame < f(at) - 6 || frame > f(out) + 32) return null;
  const D = from === "top" || from === "bottom" ? 620 : 980;
  const vx = from === "left" ? -1 : from === "right" ? 1 : 0;
  const vy = from === "top" ? -1 : from === "bottom" ? 1 : 0;
  const pend = Math.sin((frame - f(at)) / 40) * 0.5 * sIn * (1 - sOut);
  const dx = vx * D * (1 - sIn) + vx * D * 1.5 * sOut;
  const dy = vy * D * (1 - sIn) + vy * D * 1.5 * sOut;
  return (
    <div style={{
      position: "absolute", left: x, top: y,
      transform: `translate(${dx}px, ${dy}px) rotate(${tilt + pend + 3 * (1 - sIn) * (vx || 1)}deg)`,
      transformOrigin: "50% -18%",
      opacity: Math.min(1, sIn * 2.2) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      <Card seed={seed} w={w} h={h} bg={bg} amp={amp}>{children}</Card>
    </div>
  );
};

/* ══ WORDS ═════════════════════════════════════════════════════ */
const Word: React.FC<{
  idx: number; size: number; color?: string; weight?: number;
  strike?: boolean; underline?: boolean; outAt?: number;
}> = ({ idx, size, color = INK, weight = 800, strike, underline, outAt }) => {
  const [start] = W[idx];
  const frame = useStep();
  const { fps } = useVideoConfig();
  const sIn = spring({ frame: frame - f(start), fps, config: { damping: 9, stiffness: 150, mass: 0.8 } });
  const sOut = outAt ? spring({ frame: frame - f(outAt), fps, config: { damping: 200, stiffness: 80, mass: 0.5 } }) : 0;
  const grow = interpolate(frame, [f(start) + 2, f(start) + 12], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  return (
    <span style={{
      position: "relative", display: "inline-block",
      fontFamily: deva, fontWeight: weight, fontSize: size, lineHeight: 1.34, color,
      // ⚡ v11: no cream halo. The type sits on paper now, so it can be clean ink.
      opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
      transform: `translateY(${(1 - sIn) * 30}px) rotate(${7 * (1 - sIn) - 12 * sOut}deg) scale(${0.88 + sIn * 0.12})`,
      transformOrigin: "50% 140%",
    }}>
      {W[idx][2]}
      {strike && <span style={{ position: "absolute", left: -4, top: "53%", height: Math.max(4, size * 0.075), width: `calc((100% + 8px) * ${grow})`, background: KUMKUM, borderRadius: 4 }} />}
      {underline && <span style={{ position: "absolute", left: 0, bottom: -6, height: 5, width: `calc(100% * ${grow})`, background: GOLD, borderRadius: 3, opacity: 0.85 }} />}
    </span>
  );
};

const Row: React.FC<{ children: React.ReactNode; gap?: number }> = ({ children, gap = 16 }) => (
  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: `0 ${gap}px` }}>{children}</div>
);

/* ══ PASTED PROPS ══════════════════════════════════════════════ */
type SwingCfg = { inAt: number; outAt: number; from?: "left" | "right" | "top"; amp?: number; travel?: number };
const useSwing = ({ inAt, outAt, from = "left", amp = 17, travel = 240 }: SwingCfg) => {
  const frame = useStep();
  const sIn = useSpringAt(inAt, 8, 95, 1.15);
  const sOut = useSpringAt(outAt, 200, 75, 0.5);
  const sign = from === "right" ? -1 : 1;
  const pend = Math.sin((frame - f(inAt)) / 34) * 1.4 * sIn * (1 - sOut);
  const dx = from === "top" ? 0 : travel * sign * (1 - sIn) - travel * 1.5 * sign * sOut;
  const dy = from === "top" ? -travel * (1 - sIn) + travel * 2 * sOut : 70 * (1 - sIn) + 190 * sOut;
  return {
    transform: `translate(${dx}px, ${dy}px) rotate(${amp * sign * (1 - sIn) + pend - 30 * sign * sOut}deg) scale(${0.9 + 0.1 * sIn - 0.12 * sOut})`,
    transformOrigin: "50% -28%",
    opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
  } as React.CSSProperties;
};

/** A cutout pasted onto its own torn card — the card is inset behind the art so the paper
    reads as backing, not as a frame. `bleed` is how far the art overhangs the paper. */
const Pasted: React.FC<{
  src: string; seed: string; h?: number; w?: number; left: number; top: number;
  cardBg?: string; tilt?: number; bleed?: number;
} & SwingCfg> = ({ src, seed, h, w, left, top, cardBg = PAPER, tilt = 0, bleed = 26, ...cfg }) => {
  const style = useSwing(cfg);
  const frame = useStep();
  if (frame < f(cfg.inAt) - 4 || frame > f(cfg.outAt) + 30) return null;
  return (
    <div style={{ position: "absolute", left, top, ...style }}>
      <div style={{ position: "relative", display: "inline-block" }}>
        {/* the card, inset from the art on every side */}
        <div style={{ position: "absolute", inset: bleed, transform: `rotate(${tilt}deg)` }}>
          <Card seed={seed} w="100%" h="100%" bg={cardBg} amp={2.6} />
        </div>
        <Img src={staticFile(src)} style={{
          position: "relative", display: "block",
          ...(h ? { height: h } : {}), ...(w ? { width: w } : {}),
          filter: "drop-shadow(0 10px 12px rgba(72,46,16,0.30))",
        }} />
      </div>
    </div>
  );
};

/* ══ PERSISTENT OBJECTS ════════════════════════════════════════ */
const PersistentRishi: React.FC = () => {
  const frame = useStep();
  const sIn = useSpringAt(3.9, 8, 90);
  const sOut = useSpringAt(10.2, 200, 70, 0.5);
  const left = useKeys([[3.9, 471], [5.6, 471], [6.3, 158], [10.2, 158]]);
  const top = useKeys([[3.9, 148], [5.6, 148], [6.3, 214], [10.2, 214]]);
  const h = useKeys([[3.9, 372], [5.6, 372], [6.3, 318], [10.2, 318]]);
  if (frame < f(3.7) || frame > f(10.9)) return null;
  const pend = Math.sin((frame - f(3.9)) / 34) * 1.3 * sIn;
  return (
    <div style={{
      position: "absolute", left, top,
      transform: `translate(${-260 * (1 - sIn)}px, ${-300 * (1 - sIn) + 200 * sOut}px) rotate(${13 * (1 - sIn) + pend - 26 * sOut}deg)`,
      transformOrigin: "50% -28%",
      opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      <div style={{ position: "relative", display: "inline-block" }}>
        <div style={{ position: "absolute", inset: 24 }}>
          <Card seed="rishi-card" w="100%" h="100%" amp={2.4} />
        </div>
        <Img src={staticFile("sanatan/rishi.png")} style={{ position: "relative", display: "block", height: h, filter: "drop-shadow(0 10px 12px rgba(72,46,16,0.30))" }} />
      </div>
    </div>
  );
};

/** Unchanged graphic match: bloodline → dot → inheritance V. One object, three meanings. */
const PersistentThread: React.FC = () => {
  const frame = useStep();
  const Y = 396, X0 = 372, X1 = 928;
  const CX = 448, PY = 232, CY = 388;
  const draw = useKeys([[6.6, 0], [7.7, 1]]);
  const collapse = useKeys([[10.0, 0], [10.5, 1]]);
  const reopen = useKeys([[16.6, 0], [17.15, 1]]);
  const sway = Math.sin(frame / 21) * 9 * draw * (1 - collapse);
  if (frame < f(6.5)) return null;
  const dotX = interpolate(collapse, [0, 1], [(X0 + X1) / 2, 640]);
  const dotY = interpolate(collapse, [0, 1], [Y - 26, 300]);
  const parkX = interpolate(reopen, [0, 1], [dotX, CX]);
  const parkY = interpolate(reopen, [0, 1], [dotY, CY]);
  const lineEnd = X0 + (X1 - X0) * draw;
  const vis = 1 - collapse;
  return (
    <svg width={1280} height={720} style={{ position: "absolute", pointerEvents: "none" }}>
      <g opacity={vis}>
        <path d={`M${X0} ${Y} Q ${(X0 + X1) / 2} ${Y - 26 - sway} ${lineEnd} ${Y - (26 + sway) * Math.sin(Math.PI * draw)}`}
          fill="none" stroke={MARIGOLD} strokeWidth={5} strokeLinecap="round" />
        {[0.2, 0.4, 0.6, 0.8].map((t, i) => draw > t ? (
          <circle key={i} cx={X0 + (X1 - X0) * t} cy={Y - (26 + sway) * Math.sin(Math.PI * t)} r={7} fill="#FBF3E2" stroke={GOLD} strokeWidth={3} />
        ) : null)}
      </g>
      <circle cx={parkX} cy={parkY} r={7 * collapse * (1 - reopen) + 4 * reopen} fill={MARIGOLD} opacity={collapse} />
      <g opacity={reopen}>
        <line x1={CX} y1={CY} x2={CX - 110 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={MARIGOLD} strokeWidth={5} strokeLinecap="round" />
        <line x1={CX} y1={CY} x2={CX + 110 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={MARIGOLD} strokeWidth={5} strokeLinecap="round" />
      </g>
    </svg>
  );
};

const Beat: React.FC<{ from: number; to: number; children: React.ReactNode }> = ({ from, to, children }) => {
  const frame = useStep();
  if (frame < f(from - 0.55) || frame >= f(to + 0.7)) return null;
  return <AbsoluteFill>{children}</AbsoluteFill>;
};

const CAM: [number, number, number, number][] = [
  [0, 1.09, 0, 26], [1.4, 1.0, 0, 0], [3.92, 1.06, 22, -12], [5.82, 1.0, -18, 8],
  [10.58, 1.05, 12, -10], [15.76, 1.0, -14, 6], [21.1, 1.08, 8, -8],
];
const useCamera = () => {
  const frame = useStep();
  const t = CAM.map((c) => f(c[0]));
  const e = { extrapolateLeft: "clamp" as const, extrapolateRight: "clamp" as const, easing: Easing.inOut(Easing.quad) };
  return `translate(${interpolate(frame, t, CAM.map((c) => c[2]), e)}px, ${interpolate(frame, t, CAM.map((c) => c[3]), e)}px) scale(${interpolate(frame, t, CAM.map((c) => c[1]), e)})`;
};

/* ══ BEATS ═════════════════════════════════════════════════════ */
const BeatA: React.FC = () => (
  <>
    {/* ANCHOR FIRST — the manuscript is already pasted at frame 0, large. */}
    <Pasted src="sanatan/manuscript.png" seed="ms" w={700} left={310} top={368}
      inAt={-0.4} outAt={3.55} from="right" amp={12} travel={120} tilt={-1.6} bleed={34} />
    {/* the headline panel — leaves the right third of the walked ground uncovered */}
    <Panel seed="pA" at={-0.35} out={3.62} x={62} y={92} w={800} h={252} from="left" tilt={-1.1}>
      <div style={{ padding: "26px 30px 0" }}>
        <Row gap={13}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Word key={i} idx={i} size={34} color={MUTED_C} weight={600} underline={i === 2} outAt={3.55} />)}</Row>
        <div style={{ height: 10 }} />
        {/* oversized cutout headline — deliberately overhangs the torn edge */}
        <div style={{ marginLeft: -34, marginRight: -34 }}>
          <Row gap={12}>
            <Word idx={8} size={78} weight={900} outAt={3.56} />
            <Word idx={9} size={78} weight={900} outAt={3.58} />
            <Word idx={10} size={78} weight={900} color={KUMKUM} strike outAt={3.6} />
            <Word idx={11} size={78} weight={900} outAt={3.62} />
            <Word idx={12} size={78} weight={900} outAt={3.64} />
          </Row>
        </div>
      </div>
    </Panel>
    <Tape x={96} y={78} rot={-21} />
  </>
);

const BeatB: React.FC = () => (
  <>
    {/* one word, one card, held big — the beat is a breath, not a layout */}
    <Panel seed="pB" at={4.2} out={5.52} x={392} y={520} w={496} h={132} bg={PAPER} from="bottom" tilt={1.4}>
      <div style={{ paddingTop: 22, textAlign: "center" }}>
        <Row><Word idx={15} size={74} weight={900} color={KUMKUM} underline outAt={5.5} /></Row>
      </div>
    </Panel>
  </>
);

const ChainLabel: React.FC<{ text: string; at: number; x: number; y: number; size?: number; color?: string; weight?: number }> =
  ({ text, at, x, y, size = 21, color = MUTED_C, weight = 700 }) => {
    const sIn = useSpringAt(at, 9, 160, 0.8);
    const sOut = useSpringAt(10.2, 200, 80, 0.5);
    return (
      <div style={{
        position: "absolute", left: x - 90, top: y, width: 180, textAlign: "center",
        opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
        transform: `translateY(${(1 - sIn) * 18 + sOut * 60}px) rotate(${6 * (1 - sIn)}deg)`,
        transformOrigin: "50% 140%",
      }}>
        <Card seed={`chain${text}`} w="100%" h={38} amp={4} tilt={((x % 7) - 3) * 0.5}>
          <div style={{ fontFamily: deva, fontSize: size, fontWeight: weight, color, lineHeight: "38px" }}>{text}</div>
        </Card>
      </div>
    );
  };

const CHAIN_X0 = 372, CHAIN_X1 = 928, CHAIN_Y = 396;
const CHAIN: [string, number, number][] = [
  ["पुत्र", 0.2, 6.80], ["पौत्र", 0.4, 6.98], ["प्रपौत्र", 0.6, 7.16], ["कई पीढ़ी", 0.8, 7.36],
];

const BeatC: React.FC = () => (
  <>
    <Pasted src="sanatan/modern_man.png" seed="mm" h={336} left={952} top={200}
      inAt={7.0} outAt={10.25} from="right" amp={16} tilt={2.2} bleed={24} />
    <ChainLabel text="ऋषि" at={6.30} x={262} y={548} size={26} color={INK} weight={800} />
    {CHAIN.map(([t, frac, at]) => (
      <ChainLabel key={t} text={t} at={at} x={CHAIN_X0 + (CHAIN_X1 - CHAIN_X0) * frac} y={CHAIN_Y - 84} />
    ))}
    <ChainLabel text="आप" at={7.52} x={1034} y={548} size={26} color={KUMKUM} weight={800} />
    <Panel seed="pC" at={8.1} out={10.22} x={360} y={444} w={580} h={106} from="bottom" tilt={-0.8}>
      <div style={{ paddingTop: 14, textAlign: "center" }}>
        <Row gap={13}>
          <Word idx={26} size={48} weight={800} outAt={10.18} />
          <Word idx={27} size={48} weight={800} outAt={10.2} />
          <Word idx={28} size={60} weight={900} color={KUMKUM} underline outAt={10.22} />
        </Row>
      </div>
    </Panel>
  </>
);

const BeatDE: React.FC = () => {
  const tag = useSpringAt(11.61, 10, 150);
  const tagOut = useSpringAt(15.4, 200, 80);
  return (
    <>
      <Pasted src="sanatan/couple.png" seed="cp" w={392} left={444} top={54}
        inAt={10.65} outAt={15.42} from="top" amp={11} travel={280} tilt={-1.4} bleed={22} />
      {/* the prohibition beat gets the only INK field in the film — the frame goes grave */}
      <Panel seed="pDE" at={11.0} out={15.45} x={198} y={352} w={884} h={244} bg={INK} from="left" tilt={0.9} amp={1.2}>
        <div style={{ paddingTop: 26, textAlign: "center" }}>
          <div style={{
            display: "inline-block", fontFamily: deva, fontSize: 21, fontWeight: 800, wordSpacing: 8,
            color: INK, background: "#F2E3B8", padding: "5px 26px",
            opacity: Math.min(1, tag * 1.5) * (1 - tagOut),
            transform: `translateY(${(1 - tag) * 18}px) rotate(${-1.5 + 5 * (1 - tag)}deg)`,
            clipPath: tornClip("tag", 6, 10),
          }}>{W[34][2]}</div>
          <div style={{ height: 14 }} />
          <Row gap={18}>
            <Word idx={38} size={70} weight={900} color="#F7EFDD" outAt={15.4} />
            <Word idx={39} size={70} weight={900} color="#F7EFDD" outAt={15.42} />
            <Word idx={40} size={70} weight={900} color="#E8A33F" outAt={15.44} />
          </Row>
        </div>
      </Panel>
      <Tape x={236} y={338} rot={16} op={0.42} />
    </>
  );
};

const BeatF: React.FC = () => {
  const frame = useStep();
  const CX = 448, PY = 232, CY = 388;
  const pA = useSpringAt(15.9, 9, 140);
  const pB = useSpringAt(16.2, 9, 140);
  const kid = useSpringAt(17.15, 8, 150);
  const term = useSpringAt(17.43, 10, 120);
  const bob = Math.sin(frame / 24) * 5;
  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-20} width={14} height={40} rx={7} fill={fill} opacity={opacity} />
  );
  const Carrier: React.FC<{ x: number; s: number; ph: number }> = ({ x, s, ph }) => (
    <g opacity={s} transform={`translate(${x},${PY + Math.sin((frame + ph) / 26) * 6}) scale(${0.8 + s * 0.2}) rotate(${8 * (1 - s)})`}>
      <circle r={44} fill="#FDF8EC" stroke={INK} strokeWidth={4} />
      <Allele x={-15} fill={INK} opacity={0.22} />
      <Allele x={15} fill={KUMKUM} />
    </g>
  );
  return (
    <>
      <Pasted src="sanatan/dna.png" seed="dna" h={520} left={940} top={110}
        inAt={15.85} outAt={22} from="right" amp={14} tilt={1.8} bleed={30} />
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 128} s={pA} ph={0} />
        <Carrier x={CX + 128} s={pB} ph={40} />
        <g opacity={kid} transform={`translate(${CX},${CY + bob}) scale(${0.78 + kid * 0.22}) rotate(${-10 * (1 - kid)})`}>
          <circle r={46} fill={KUMKUM} opacity={0.14} />
          <circle r={46} fill="none" stroke={KUMKUM} strokeWidth={5} />
          <Allele x={-15} fill={KUMKUM} />
          <Allele x={15} fill={KUMKUM} />
        </g>
      </svg>
      <Panel seed="pF" at={17.4} out={22} x={72} y={478} w={742} h={158} from="bottom" tilt={-1.2}>
        <div style={{
          paddingTop: 22, textAlign: "center",
          opacity: Math.min(1, term * 1.4),
        }}>
          <div style={{ fontFamily: inter, fontSize: 42, fontWeight: 800, letterSpacing: 6, color: KUMKUM }}>CONSANGUINITY</div>
          <div style={{ height: 8 }} />
          <div style={{ fontFamily: deva, fontSize: 25, fontWeight: 700, color: INK, opacity: 0.82 }}>विज्ञान ने इसे एक नाम दिया।</div>
        </div>
      </Panel>
    </>
  );
};

const Furniture: React.FC = () => {
  const frame = useStep();
  const { durationInFrames } = useVideoConfig();
  const p = interpolate(frame, [0, durationInFrames], [0, 1]);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div style={{ position: "absolute", top: 40, left: 48, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 18, background: MARIGOLD, borderRadius: 2 }} />
        <span style={{ fontFamily: deva, fontWeight: 700, fontSize: 17, wordSpacing: 7, color: INK, opacity: 0.62 }}>सनातन डिकोड</span>
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${p * 100}%`, background: MARIGOLD }} />
    </AbsoluteFill>
  );
};

export const SanatanDecodeGotraV11: React.FC = () => {
  const cam = useCamera();
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      <Audio src={staticFile("sanatan/gotra.mp3")} />
      <Pano />
      <AbsoluteFill style={{ transform: cam }}>
        <Beat from={0} to={3.75}><BeatA /></Beat>
        <Beat from={3.9} to={5.6}><BeatB /></Beat>
        <Beat from={5.8} to={10.4}><BeatC /></Beat>
        <Beat from={10.6} to={15.6}><BeatDE /></Beat>
        <Beat from={15.8} to={21.1}><BeatF /></Beat>
        <PersistentRishi />
        <PersistentThread />
      </AbsoluteFill>
      <Petals />
      <Grain />
      <Furniture />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
